// OutreachModal — coach-only "Express interest" form.
//
// M5.1 (discovery-only) + M6.2-followup-2 (broadened per Kaillum's
// 2026-06-07 directive): coaches now express interest on ANY player.
// Subtitle branches on playerHasAgent so the copy reads correctly for
// both represented and discovery players. Backend (/api/outreach)
// accepts both as of the same commit; the player_has_agent 403 was
// dropped.
//
// Renders a centered modal with a textarea + live character counter.
// Submit POSTs to /api/outreach with { player_id, message };
// on 201 → onSuccess() then onClose(). On any non-2xx → surface the
// error inline and keep the modal open so the coach can retry.
//
// State ownership: the modal owns message/pending/error state. The
// "already-sent" gate lives upstream in PlayerProfile (which hides the
// CTA when outreach_already_sent_by_viewer is true).

import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '../../lib/api'

const MIN_LEN = 10
const MAX_LEN = 1000

interface Props {
  open: boolean
  playerId: string
  playerName: string
  // M6.2-followup-2 — true when the player already has an agent. Drives
  // the subtitle copy fork: represented → "routed to their agent",
  // unrepresented → "surfaced to incoming agents".
  playerHasAgent: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function OutreachModal({
  open,
  playerId,
  playerName,
  playerHasAgent,
  onClose,
  onSuccess,
}: Props) {
  const [message, setMessage] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  // A4 (a11y) — the trigger to restore focus to on close, and the dialog root
  // used to scope the focus trap.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)

  // Reset state every time the modal opens. Keeps an aborted draft
  // from a prior open from sneaking into a re-open. Also captures the trigger
  // and restores focus to it on close (A4).
  useEffect(() => {
    if (!open) return
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null
    setMessage('')
    setError(null)
    setPending(false)
    // Defer focus so the browser has actually painted the modal.
    const t = window.setTimeout(() => textareaRef.current?.focus(), 0)
    return () => {
      window.clearTimeout(t)
      previouslyFocusedRef.current?.focus?.()
    }
  }, [open])

  // Escape closes the modal (unless a request is in flight — better
  // not to lose the draft mid-submit). Background click also closes.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !pending) {
        onClose()
        return
      }
      // A4 — trap Tab within the dialog so keyboard focus can't escape to
      // the page behind the modal.
      if (e.key === 'Tab') {
        const root = dialogRef.current
        if (!root) return
        const focusables = root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, pending, onClose])

  if (!open) return null

  const trimmedLength = message.trim().length
  const lengthValid = trimmedLength >= MIN_LEN && trimmedLength <= MAX_LEN

  async function submit(): Promise<void> {
    if (!lengthValid || pending) return
    setPending(true)
    setError(null)
    try {
      const res = await apiFetch('/api/outreach', {
        method: 'POST',
        body: JSON.stringify({
          player_id: playerId,
          message: message.trim(),
        }),
      })
      if (res.status === 201) {
        onSuccess()
        onClose()
        return
      }
      // Anything else: try to surface the server's error key; otherwise
      // a generic message. We DON'T close — the coach can retry / fix.
      const body = (await res.json().catch(() => null)) as
        | { error?: string }
        | null
      const key = body?.error
      // M6.2-followup-2 — dropped the `player_has_agent` branch; the
      // backend no longer 403s on represented players.
      const friendly =
        key === 'outreach_already_sent'
          ? 'Interest already expressed in this player.'
          : key === 'message_length_invalid'
            ? `Message must be ${MIN_LEN}–${MAX_LEN} characters.`
            : key === 'role_mismatch'
              ? 'Only coaches can express interest.'
              : key === 'unauthorized'
                ? 'Please sign in to express interest.'
                : key === 'player_not_found'
                  ? 'Player not found.'
                  : `Request failed (${res.status})`
      setError(friendly)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Network error')
    } finally {
      setPending(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={() => {
        if (!pending) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="outreach-modal-title"
    >
      <div
        ref={dialogRef}
        className="bg-hoop-surface border border-hoop-border rounded-2xl shadow-xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="outreach-modal-title"
          className="text-lg font-semibold text-hoop-text"
        >
          Express interest in {playerName}
        </h2>
        <p className="mt-1 text-xs text-hoop-text-3">
          {playerHasAgent
            ? `Your note goes on record against ${playerName}. It doesn't notify the agent yet — contact still happens directly with ${playerName}'s agent of record.`
            : `${playerName} doesn't have agent representation on HoopRoster yet. Your note goes on record and shows us which players coaches actually want.`}
        </p>

        <label className="mt-4 block">
          <textarea
            ref={textareaRef}
            aria-label="Your note about this player"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={pending}
            rows={6}
            placeholder="Why this player fits your program — kept on record with your interest. (10–1000 characters)"
            className="w-full border border-hoop-border rounded-md px-3 py-2 text-sm bg-hoop-surface text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent disabled:opacity-50"
            maxLength={MAX_LEN * 2}
          />
        </label>

        <div className="mt-1 flex items-center justify-between text-xs">
          <span
            className={
              trimmedLength === 0
                ? 'text-hoop-text-3'
                : !lengthValid
                  ? 'text-hoop-red'
                  : 'text-hoop-text-3'
            }
          >
            {trimmedLength} / {MAX_LEN}
            {trimmedLength > 0 && trimmedLength < MIN_LEN && (
              <span> &mdash; minimum {MIN_LEN}</span>
            )}
            {trimmedLength > MAX_LEN && (
              <span> &mdash; over the limit</span>
            )}
          </span>
        </div>

        {error && (
          <div className="mt-3 border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-3 py-2 text-xs">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="px-4 py-2 text-sm rounded-md border border-hoop-border text-hoop-text-2 hover:bg-hoop-bg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!lengthValid || pending}
            className="px-4 py-2 text-sm rounded-md bg-hoop-accent text-hoop-primary-fg hover:opacity-90 disabled:opacity-40"
          >
            {pending ? 'Saving…' : 'Record interest'}
          </button>
        </div>
      </div>
    </div>
  )
}
